<template>
  <div class="flex items-center justify-center min-h-screen bg-theme-bg2 p-4">
    <div class="w-full max-w-sm">
      <div class="bg-theme-card border border-theme-border rounded-2xl overflow-hidden">
        <div class="h-1 bg-gradient-to-r from-primary-600 to-secondary-500"></div>
        <div class="p-7">
          <div class="flex justify-center items-center mb-5">
            <img :src="isDark ? darkLogo : lightLogo" alt="logo" class="h-10 w-auto">
          </div>
          <div class="text-center mb-7">
            <h2 class="text-2xl font-bold text-theme-text">Set New Password</h2>
            <p class="text-sm text-theme-muted mt-1">Choose a strong new password</p>
          </div>
      <form @submit.prevent="resetPassword">
        <div class="mb-4">
          <label for="new-password" class="block text-sm font-medium text-theme-text2">New Password</label>
          <input
            type="password"
            id="new-password"
            v-model="newPassword"
            required
            minlength="8"
            class="mt-1 block w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 bg-theme-input text-theme-input-text border-theme-input-border"
            placeholder="Enter new password (min 8 characters)"
            @input="validatePassword"
          />
          <p v-if="passwordError" class="text-red-500 text-xs mt-1">{{ passwordError }}</p>
        </div>
        <div class="mb-4">
          <label for="confirm-password" class="block text-sm font-medium text-theme-text2">Confirm Password</label>
          <input
            type="password"
            id="confirm-password"
            v-model="confirmPassword"
            required
            minlength="8"
            class="mt-1 block w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 bg-theme-input text-theme-input-text border-theme-input-border"
            placeholder="Confirm your password"
            @input="validatePassword"
          />
        </div>
        <button
          type="submit"
          :disabled="loading"
          class="w-full bg-primary-600 text-white font-medium py-2 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          {{ loading ? "Resetting..." : "Reset Password" }}
        </button>
      </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import axios from "axios";
import { useRoute, useRouter } from "vue-router";
import { useTheme } from '@/composables/useTheme.js';
import useNotifications from '@/store/notification';
import darkLogo from '@/assets/images/dark_logo.png';
import lightLogo from '@/assets/images/logo.jpeg';

export default {
  setup() {
    const { isDark } = useTheme();
    const newPassword = ref("");
    const confirmPassword = ref("");
    const passwordError = ref("");
    const api_base_url = import.meta.env.VITE_API_BASE_URL;
    const { showNotification } = useNotifications();

    const loading = ref(false);
    
    const route = useRoute();
    const router = useRouter();

    // Extract uidb64 and token from the route parameters
    const { uid, token } = route.query;

    const validatePassword = () => {
      if (newPassword.value.length < 8) {
        passwordError.value = "Password must be at least 8 characters long";
        return false;
      }
      
      if (newPassword.value !== confirmPassword.value) {
        passwordError.value = "Passwords do not match";
        return false;
      }
      
      passwordError.value = "";
      return true;
    };

    const resetPassword = async () => {
      if (!validatePassword()) {
        return;
      }

      loading.value = true;

      try {
        // Send password reset request to backend
        const response = await axios.post(
          `${api_base_url}/api/accounts/password-reset/confirm/${uid}/${token}/`,
          {
            new_password: newPassword.value,
            confirm_password: confirmPassword.value,
          }
        );
        router.push({ name: 'login' });
        showNotification('Password Reset', `${response.data.message}` || "Password was reset successfully!", 'success');

      } catch (err) {
        showNotification('Password Reset', `${err.response?.data?.error}` || "Failed to reset password. Please try again.", 'error');
      } finally {
        loading.value = false;
      }
    };

    return {
      newPassword,
      confirmPassword,
      passwordError,
      loading,
      isDark,
      darkLogo,
      lightLogo,
      resetPassword,
      validatePassword,
    };
  },
};
</script>

<style scoped>
/* Add additional styling if necessary */
</style>